// ─────────────────────────────────────────────────────────────────
// LandingPage.jsx — Página de inicio KIN
// ─────────────────────────────────────────────────────────────────

import { useState, useRef } from "react";
import { HS_SUBHEADINGS } from "./lib/hsSubheadings";
import { HS_HEADINGS, HS_CHAPTERS } from "./lib/hsDescriptions";
import LegalModal from "./LegalDocs";
import PlanModal from "./PlanModal";

// ── Claude API helpers ───────────────────────────────────────────
async function callClaude(system, user, maxTokens = 600) {
  const res = await fetch('/api/claude', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'claude-3-5-haiku-20241022',
      max_tokens: maxTokens,
      system,
      messages: [{ role: 'user', content: user }],
    }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  return (data.content || []).filter(b => b.type === 'text').map(b => b.text).join('\n');
}

function extractJSON(text) {
  const clean = text.replace(/```json\s*/gi, '').replace(/```\s*/g, '').trim();
  const m = clean.match(/\{[\s\S]*\}/);
  return m ? JSON.parse(m[0]) : {};
}

// Quita tildes para que "cafe" encuentre "café" y viceversa
function stripAccents(str) {
  return str.normalize('NFD').replace(/[̀-ͯ]/g, '');
}

// ¿"word" aparece como palabra completa dentro de "text"? (evita que "movil"
// dispare con "automovil", o "ropa" con "europa")
function hasWholeWord(text, word) {
  const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|[^a-z0-9])${escaped}([^a-z0-9]|$)`).test(text);
}

function getHSIcon(code) {
  const ch = parseInt((code || '').replace(/\D/g,'').slice(0,2), 10);
  if (ch >= 1  && ch <= 5 )  return '🥩';
  if (ch === 6)               return '🌹';
  if (ch >= 7  && ch <= 8 )  return '🥦';
  if (ch === 9)               return '☕';
  if (ch === 10)              return '🌾';
  if (ch >= 11 && ch <= 14)  return '🌿';
  if (ch >= 15 && ch <= 24)  return '🫙';
  if (ch >= 25 && ch <= 27)  return '⛏️';
  if (ch >= 28 && ch <= 40)  return '🧪';
  if (ch >= 41 && ch <= 43)  return '👜';
  if (ch >= 44 && ch <= 49)  return '📄';
  if (ch >= 50 && ch <= 63)  return '👗';
  if (ch >= 64 && ch <= 67)  return '👟';
  if (ch >= 68 && ch <= 70)  return '🏗️';
  if (ch >= 71 && ch <= 71)  return '💎';
  if (ch >= 72 && ch <= 83)  return '🔩';
  if (ch >= 84 && ch <= 85)  return '⚙️';
  if (ch >= 86 && ch <= 89)  return '🚗';
  if (ch >= 90 && ch <= 92)  return '🔬';
  if (ch >= 93 && ch <= 96)  return '📦';
  return '📦';
}

const C = {
  navy:  '#0A2540',
  teal:  '#0EA5A4',
  amber: '#D4A24C',
  cloud: '#FAFAF7',
  grey:  '#64748B',
  light: '#E2E8F0',
  white: '#FFFFFF',
};

const BENTO_PRODUCTS = [
  { hs:'0901.11', name:'Café verde sin descafeinar',     val:'$312M USD', trend:'↑ 8.4% vs año anterior',  trendUp:true  },
  { hs:'0804.40', name:'Aguacate Hass',                   val:'$180M USD', trend:'↑ 22.1% — en auge',        trendUp:true  },
  { hs:'0603.11', name:'Rosas frescas',                   val:'$94M USD',  trend:'↑ 5.2% — estable',         trendUp:true  },
  { hs:'2701.12', name:'Carbón mineral bituminoso',       val:'$1.1B USD', trend:'↓ 3.8% — tendencia baja',  trendUp:false },
  { hs:'0803.90', name:'Banano tipo Cavendish',           val:'$86M USD',  trend:'↑ 11.7% — mercado EE.UU.', trendUp:true  },
  { hs:'2709.00', name:'Petróleo crudo',                  val:'$8.4B USD', trend:'↓ 12.1% — baja demanda',   trendUp:false },
];

const FEATURES = [
  { icon:'🏛️', title:'Datos DIAN',          desc:'Importaciones y exportaciones reales de Colombia: empresas, cantidades, valores, puertos y más.' },
  { icon:'🌐', title:'UN Comtrade',          desc:'Flujos bilaterales entre 180+ países. Compara Colombia vs el mercado mundial.' },
  { icon:'📜', title:'Aranceles OMC',        desc:'Tasas arancelarias aplicadas y consolidadas para cada código HS en cada destino.' },
  { icon:'🤖', title:'Análisis IA',          desc:'Claude AI sintetiza tendencias, riesgos y oportunidades en lenguaje claro.' },
  { icon:'🧮', title:'Calculadora de costos',desc:'Estima el costo total de importar o exportar: arancel + flete + seguro + IVA.' },
  { icon:'📅', title:'Estacionalidad',       desc:'Identifica los mejores meses para negociar o lanzar productos según historial de 3 años.' },
];

const PLANS = [
  {
    name:'Demo', price:'Gratis', period:'', desc:'Explora la plataforma sin registro.',
    feats:['Exportaciones de flores · datos reales DIAN','Sin registro requerido','1 producto de muestra'],
    featured:false, btnLabel:'Ver demo →', btnAction:'demo',
  },
  {
    name:'Perfil de empresa', price:'$30', period:'/empresa · 1 año', desc:'¿Solo necesitas ver una empresa puntual?',
    feats:['Cifras reales de una NIT específica','Ranking de compradores y proveedores','Acceso por 1 año, sin suscripción'],
    featured:false, btnLabel:'Buscar una empresa →', btnAction:'buscarEmpresa',
  },
  {
    name:'Profesional', price:'$49', period:'/mes', desc:'Para importadores y exportadores activos.',
    feats:['Búsquedas ilimitadas','Datos DIAN completos','NITs y empresas competidoras','Análisis IA completo','Logística internacional','Exportar Excel / PDF'],
    featured:true, btnLabel:'Empezar Plan Pro →', btnAction:'signup',
  },
  {
    name:'Corporativo', price:'$99', period:'/mes', desc:'Para agencias y grandes empresas.',
    feats:['Todo en Profesional','Hasta 10 usuarios','Soporte prioritario','Onboarding personalizado'],
    featured:false, btnLabel:'Contactar ventas', btnAction:'contact',
  },
];

const NOS_STATS = [
  { num:'+16M', lbl:'Registros procesados' },
  { num:'26',    lbl:'Meses de historia DIAN' },
  { num:'180+',  lbl:'Países en Comtrade' },
  { num:'< 3s',  lbl:'Respuesta promedio' },
];

// Aliases: términos comunes en español → código HS
const KEYWORD_ALIASES = {
  'celular':      ['851762','851761','851769','851711','851712'], 'celulares': ['851762','851761','851769','851711','851712'],
  'smartphone':   ['851762','851761','851769'], 'iphone': ['851762','851711'], 'samsung': ['851762','851769'],
  'telefono':     ['851762','851761','851769'], 'telefonos': ['851762','851761','851769'],
  'movil':        ['851762','851761'], 'moviles': ['851762','851761'],
  'cafetera':     ['851671'], 'cafeteras':    ['851671'],
  'computadora':  ['847130'], 'computadoras': ['847130'], 'laptop':       ['847130'], 'portatil': ['847130'],
  'television':   ['852872'], 'televisor':    ['852872'], 'tv':           ['852872'], 'televisores': ['852872'],
  'nevera':       ['841810'], 'refrigerador': ['841810'], 'heladera':     ['841810'],
  'lavadora':     ['845110'], 'lavadoras':    ['845110'],
  'microondas':   ['851650'], 'horno':        ['851660'],
  'carro':        ['870322'], 'coche':        ['870322'], 'automovil':    ['870322'], 'vehiculo': ['870322'],
  'moto':         ['871120'], 'motocicleta':  ['871120'],
  'arroz':        ['100630'], 'trigo':        ['100190'], 'maiz':         ['100590'],
  'aceite':       ['151190'], 'soya':         ['120100'], 'azucar':       ['170114'],
  'plastico':     ['392690'], 'pvc':          ['390410'],
  'tela':         ['520942'], 'ropa':         ['621040'], 'zapatos':      ['640399'],
  'medicamento':  ['300490'], 'medicina':     ['300490'],
  'papel':        ['480256'], 'carton':       ['481019'],
  'acero':        ['720890'], 'hierro':       ['720830'], 'aluminio':     ['760110'],
};

// Datos autocomplete (demo)
const AC_PRODUCTS = [
  { hs:'0901.11', name:'Café sin tostar, sin descafeinar',  sub:'Exportadores top: Colombia, Brasil, Vietnam', icon:'☕' },
  { hs:'0901.12', name:'Café sin tostar, descafeinado',     sub:'Café verde descafeinado',                     icon:'☕' },
  { hs:'0901.21', name:'Café tostado, sin descafeinar',     sub:'Mayor valor agregado · mercado premium',      icon:'☕' },
  { hs:'0901.22', name:'Café tostado, descafeinado',        sub:'Café tostado descafeinado',                   icon:'☕' },
  { hs:'2101.11', name:'Extractos y esencias de café',      sub:'Café soluble, concentrados',                  icon:'🫙' },
  { hs:'0804.40', name:'Aguacate Hass',                     sub:'Boom exportador · mercado europeo',            icon:'🥑' },
  { hs:'0603.11', name:'Rosas frescas cortadas',            sub:'Colombia 2.° exportador mundial',              icon:'🌹' },
  { hs:'0803.90', name:'Banano tipo Cavendish',             sub:'Mercado principal: EE.UU.',                    icon:'🍌' },
  { hs:'2701.12', name:'Carbón mineral bituminoso',         sub:'Principal producto de exportación',            icon:'⛏️' },
  { hs:'2709.00', name:'Petróleo crudo',                    sub:'Alta volatilidad · mercado global',            icon:'🛢️' },
  { hs:'8542.31', name:'Circuitos integrados / semiconductores', sub:'Chips, microprocesadores, memoria',       icon:'🔬' },
  { hs:'8471.30', name:'Computadoras portátiles',           sub:'Alta demanda · importación frecuente',         icon:'💻' },
  { hs:'8703.21', name:'Automóviles cilindrada ≤ 1.000 cm³',        sub:'Vehículos de turismo · DIAN Colombia', icon:'🚗' },
  { hs:'8703.22', name:'Automóviles cilindrada 1.000–1.500 cm³',    sub:'Vehículos de turismo · DIAN Colombia', icon:'🚗' },
  { hs:'8703.23', name:'Automóviles cilindrada 1.500–3.000 cm³',    sub:'Importación masiva · mercado Colombia', icon:'🚗' },
  { hs:'8703.24', name:'Automóviles cilindrada > 3.000 cm³',        sub:'Vehículos de turismo · DIAN Colombia', icon:'🚗' },
  { hs:'8703.40', name:'Vehículos híbridos (gasolina + eléctrico)', sub:'Vehículos electrificados · DIAN',      icon:'🚗' },
  { hs:'8703.50', name:'Vehículos híbridos (diésel + eléctrico)',   sub:'Vehículos electrificados · DIAN',      icon:'🚗' },
  { hs:'8703.60', name:'Vehículos eléctricos puros',                sub:'Cero emisiones · DIAN Colombia',       icon:'⚡' },
  { hs:'8703.90', name:'Otros vehículos para transporte de personas', sub:'Vehículos de turismo · DIAN',        icon:'🚗' },
  { hs:'0306.17', name:'Camarones congelados',              sub:'Exportación acuícola en crecimiento',          icon:'🦐' },
  { hs:'7108.12', name:'Oro en bruto',                      sub:'Alta pureza · exportación regulada',           icon:'🟡' },
];

const AC_COMPANIES = [
  { nit:'900.123.456-7', name:'Fedecafé Export S.A.S.',  city:'Bogotá',   sector:'Alimentos', exp:'$48.2M', imp:'$12.1M', countries:38 },
  { nit:'800.987.123-2', name:'Tostadores del Eje Ltda', city:'Armenia',  sector:'Alimentos', exp:'$35.1M', imp:'$8.4M',  countries:22 },
  { nit:'901.456.789-1', name:'Green Bean Export S.A.',  city:'Medellín', sector:'Alimentos', exp:'$27.4M', imp:'$5.2M',  countries:18 },
];

// ── Componentes pequeños ─────────────────────────────────────────

function Btn({ children, style: extraStyle, onClick, variant = 'primary' }) {
  const base = {
    display:'inline-flex', alignItems:'center', gap:6,
    padding:'8px 18px', borderRadius:8, fontSize:14, fontWeight:600,
    cursor:'pointer', border:'none', transition:'opacity .15s', fontFamily:'inherit',
  };
  const variants = {
    primary: { background:C.teal,  color:C.white },
    amber:   { background:C.amber, color:C.white },
    outline: { background:'transparent', border:`1.5px solid ${C.light}`, color:C.navy },
  };
  return (
    <button onClick={onClick} style={{ ...base, ...variants[variant], ...extraStyle }}>
      {children}
    </button>
  );
}

function BarRow({ label, pct, val, color = C.teal }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
      <span style={{ width:140, fontSize:13, flexShrink:0 }}>{label}</span>
      <div style={{ flex:1, height:10, background:'#f1f5f9', borderRadius:100 }}>
        <div style={{ width:`${pct}%`, height:'100%', background:color, borderRadius:100 }} />
      </div>
      <span style={{ width:70, fontSize:12, fontWeight:600, textAlign:'right' }}>{val}</span>
    </div>
  );
}

// ── NIT Modal ────────────────────────────────────────────────────

function NitModal({ company, onClose, onGoToPricing }) {
  if (!company) return null;
  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position:'fixed', inset:0, background:'rgba(10,37,64,.6)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
    >
      <div style={{ background:C.white, borderRadius:20, width:'100%', maxWidth:680, maxHeight:'90vh', overflowY:'auto', padding:32, boxShadow:'0 24px 80px rgba(0,0,0,.3)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 }}>
          <div>
            <h3 style={{ margin:0, fontSize:20 }}>{company.name}</h3>
            <p style={{ fontSize:13, color:C.grey, margin:'4px 0 0' }}>NIT {company.nit} · {company.city} · Sector: {company.sector}</p>
          </div>
          <button onClick={onClose} style={{ background:'#f1f5f9', border:'none', borderRadius:8, width:32, height:32, cursor:'pointer', fontSize:16 }}>✕</button>
        </div>

        {/* KPIs */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:24 }}>
          {[['k-val', company.exp, 'Total exportado'],['k-val', company.imp, 'Total importado'],['k-val', company.countries, 'Países']].map(([,v,l],i) => (
            <div key={i} style={{ background:'#f8fafc', borderRadius:10, padding:16, textAlign:'center' }}>
              <div style={{ fontSize:22, fontWeight:800, color:C.teal }}>{v}</div>
              <div style={{ fontSize:12, color:C.grey }}>{l}</div>
            </div>
          ))}
        </div>

        <p style={{ fontSize:13, fontWeight:600, marginBottom:8 }}>Top 3 productos exportados</p>
        <BarRow label="Café verde"  pct={88} val="$24.1M" />
        <BarRow label="Café tostado" pct={55} val="$15.0M" />
        <BarRow label="Extractos"   pct={33} val="$9.1M" color={C.teal} />

        <p style={{ fontSize:13, fontWeight:600, margin:'16px 0 8px' }}>Top 3 destinos</p>
        <BarRow label="🇺🇸 EE.UU."    pct={70} val="$18.2M" color={C.amber} />
        <BarRow label="🇩🇪 Alemania"  pct={45} val="$11.7M" color={C.amber} />
        <BarRow label="🇯🇵 Japón"     pct={28} val="$7.2M"  color={C.amber} />

        {/* Paywall */}
        <div style={{ background:'#fffbeb', border:`1.5px solid #fde68a`, borderRadius:12, padding:20, textAlign:'center', marginTop:20 }}>
          <p style={{ fontSize:14, fontWeight:700, marginBottom:6 }}>🔒 Desbloquea el perfil completo</p>
          <p style={{ fontSize:13, color:C.grey, marginBottom:12 }}>Todos los productos · 38 países · Declaraciones individuales · Precios unitarios · Exportar Excel</p>
          <Btn onClick={() => { onClose(); onGoToPricing(); }}>Ver Plan Pro →</Btn>
        </div>
      </div>
    </div>
  );
}

// ── Tabs del panel demo ──────────────────────────────────────────

function DemoPanel() {
  const [tab, setTab] = useState('resumen');

  const tabs = [
    { id:'resumen',   label:'Resumen' },
    { id:'colombia',  label:'🇨🇴 Colombia DIAN' },
    { id:'comtrade',  label:'Comtrade' },
    { id:'aranceles', label:'Aranceles' },
    { id:'ia',        label:'🤖 Análisis IA' },
  ];

  return (
    <div style={{ background:C.white, borderRadius:16, border:`1px solid ${C.light}`, overflow:'hidden', maxWidth:860, margin:'0 auto' }}>
      {/* Tab bar */}
      <div style={{ display:'flex', borderBottom:`1px solid ${C.light}`, overflowX:'auto', padding:'0 16px', background:'#f8fafc' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding:'12px 18px', fontSize:13, fontWeight:600, whiteSpace:'nowrap',
            cursor:'pointer', borderBottom:`2px solid ${tab===t.id ? C.teal : 'transparent'}`,
            color: tab===t.id ? C.teal : C.grey,
            background:'none', border:'none', borderBottom:`2px solid ${tab===t.id ? C.teal : 'transparent'}`,
            fontFamily:'inherit',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ padding:24 }}>

        {tab === 'resumen' && (
          <div>
            <h3 style={{ marginBottom:16 }}>Café verde sin descafeinar · HS 0901.11</h3>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:24 }}>
              {[['$312M','Exportaciones 2024'],['48','Países destino'],['1,240','Empresas DIAN']].map(([v,l],i) => (
                <div key={i} style={{ background:'#f8fafc', borderRadius:10, padding:16, textAlign:'center' }}>
                  <div style={{ fontSize:22, fontWeight:800, color:C.teal }}>{v}</div>
                  <div style={{ fontSize:12, color:C.grey }}>{l}</div>
                </div>
              ))}
            </div>
            <p style={{ color:C.grey, fontSize:14, lineHeight:1.7 }}>Colombia es el 3.er exportador mundial de café. Los principales destinos son EE.UU. (34%), Alemania (18%) y Japón (9%). El precio promedio FOB fue $2.18/kg en 2024, con pico en marzo-mayo.</p>
            <BlurPaywall text="Tendencia mensual, empresas líderes y precios por quintal" />
          </div>
        )}

        {tab === 'colombia' && (
          <div>
            <h3 style={{ marginBottom:20 }}>Importaciones Colombia — DIAN 2024</h3>
            <BarRow label="Fedecafé"       pct={88} val="$48.2M" />
            <BarRow label="Café Quindío"   pct={64} val="$35.1M" />
            <BarRow label="Colombian Gold" pct={50} val="$27.4M" />
            <BarRow label="Exportcafé Ltda" pct={38} val="$20.8M" />
            <BarRow label="Otras (1,236)"  pct={30} val="$16.5M" />
            <BlurPaywall text="Ver todas las empresas, NITs, ciudades y valores FOB" />
          </div>
        )}

        {tab === 'comtrade' && (
          <div>
            <h3 style={{ marginBottom:20 }}>Flujos mundiales — Comtrade 2024</h3>
            <BarRow label="Brasil"    pct={95} val="$5.8B" />
            <BarRow label="Vietnam"   pct={72} val="$4.3B" />
            <BarRow label="Colombia"  pct={53} val="$3.1B" />
            <BarRow label="Indonesia" pct={45} val="$2.7B" />
            <BarRow label="Honduras"  pct={22} val="$1.3B" />
            <p style={{ fontSize:13, color:C.grey, marginTop:12 }}>Colombia representa el 11.2% del mercado mundial de café. Principales compradores: EE.UU. 34%, Alemania 18%, Japón 9%.</p>
          </div>
        )}

        {tab === 'aranceles' && (
          <div>
            <h3 style={{ marginBottom:20 }}>Aranceles aplicados — HS 0901.11</h3>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr>{['País destino','Arancel MFN','TLC Colombia','Ahorro'].map(h => (
                  <th key={h} style={{ textAlign:'left', color:C.grey, fontWeight:600, padding:'8px 6px', borderBottom:`1px solid ${C.light}` }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {[
                  ['🇺🇸 EE.UU.','0%','0%','—'],
                  ['🇪🇺 Unión Europea','7.5%','0%','7.5%'],
                  ['🇨🇳 China','8%','Sin TLC','—'],
                  ['🇯🇵 Japón','12%','Sin TLC','—'],
                  ['🇨🇦 Canadá','0%','0%','—'],
                ].map((row,i) => (
                  <tr key={i}>{row.map((cell,j) => (
                    <td key={j} style={{ padding:'8px 6px', borderBottom:`1px solid #f8fafc`, color: j===3 && cell!=='—' ? '#16a34a' : 'inherit', fontWeight: j===3 && cell!=='—' ? 600 : 400 }}>{cell}</td>
                  ))}</tr>
                ))}
              </tbody>
            </table>
            <p style={{ fontSize:13, color:C.grey, marginTop:12 }}>El Acuerdo Comercial Colombia-UE elimina el 7.5% de arancel, ahorro anual estimado: $23M USD.</p>
          </div>
        )}

        {tab === 'ia' && (
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16 }}>
              <span style={{ fontSize:22 }}>🤖</span>
              <strong>Análisis generado por Claude AI</strong>
              <span style={{ fontSize:12, color:C.grey, marginLeft:'auto' }}>Actualizado hace 2 horas</span>
            </div>
            <div style={{ background:'#f8fafc', borderRadius:12, padding:20, fontSize:14, lineHeight:1.7, color:'#334155' }}>
              <p><strong>Oportunidad:</strong> El café colombiano mantiene una prima de precio del 18% sobre el promedio mundial. Los mercados de mayor crecimiento en 2024 son Corea del Sur (+41%) y Australia (+28%).</p>
              <p style={{ marginTop:12 }}><strong>Riesgo:</strong> La sequía en el eje cafetero (Q1 2025) proyecta una reducción del 12% en producción para el primer semestre.</p>
              <p style={{ marginTop:12 }}><strong>Precio de referencia:</strong> $2.18/kg FOB Buenaventura (promedio Feb 2025). Fuente: DIAN + Comtrade.</p>
            </div>
            <BlurPaywall text="Recomendación de timing, empresas compradoras y precios por empresa" />
          </div>
        )}

      </div>
    </div>
  );
}

function BlurPaywall({ text }) {
  const scrollToPricing = () => document.getElementById('pricing')?.scrollIntoView({ behavior:'smooth' });
  return (
    <div style={{ position:'relative', marginTop:16 }}>
      <div style={{ filter:'blur(5px)', pointerEvents:'none', userSelect:'none', fontSize:13, lineHeight:1.7, color:C.grey }}>
        {text} — datos detallados disponibles incluyen empresas, precios unitarios, tendencias por mes, comparativo anual, competidores, oportunidades de mercado.
      </div>
      <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:'rgba(250,250,247,.7)' }}>
        <div style={{ background:C.navy, color:C.white, padding:'14px 28px', borderRadius:12, textAlign:'center', boxShadow:'0 8px 32px rgba(10,37,64,.25)' }}>
          <p style={{ margin:0, fontWeight:700 }}>🔒 Desbloquea el análisis completo</p>
          <p style={{ margin:'4px 0 12px', fontSize:12, color:'#94a3b8' }}>Plan Pro · $49/mes · Cancela cuando quieras</p>
          <Btn onClick={scrollToPricing} extraStyle={{ width:'100%', justifyContent:'center' }}>Ver planes →</Btn>
        </div>
      </div>
    </div>
  );
}

// ── Autocomplete ─────────────────────────────────────────────────

function getStaticItems(val) {
  let clean    = val.trim();
  const digits = clean.replace(/\D/g, '');
  // Partida con punto: "0901.11" / "0901.113000" → tratar como código HS puro
  const hsStyle = /^\d{4}\.\d{1,6}$/.test(clean);
  if (hsStyle) clean = digits;
  const isNum  = /^\d+$/.test(clean);
  // Número puro de 10 dígitos sin separadores = subpartida arancelaria nacional completa, no NIT
  const isPureDigitsLen10 = /^\d{10}$/.test(val.trim());
  // NITs pueden venir formateados: 900.123.456-7 → 9001234567 (10 dígitos)
  const isNit  = !hsStyle && !isPureDigitsLen10 && digits.length >= 8 && /^[\d.\s\-]+$/.test(val.trim());

  if (isNit) {
    const match = AC_COMPANIES.find(c => c.nit.replace(/\D/g,'').startsWith(digits));
    return [{ type:'company', nit: digits, ...(match || { name:`Empresa NIT ${digits}`, city:'Colombia', sector:'Comercio' }) }];
  }
  if (isNum && clean.length >= 4) {
    const prefix4  = clean.slice(0, 4);
    const prefix6  = clean.length >= 6 ? clean.slice(0, 6) : null;
    const direct   = { type:'product', hs: prefix4, name:`Partida ${prefix4} · todas las subpartidas`, sub:'Búsqueda directa por código arancelario', icon:'📦' };
    const filterBy = prefix6 || prefix4;
    const subs = Object.entries(HS_SUBHEADINGS)
      .filter(([code]) => code.startsWith(filterBy))
      .map(([code, name]) => ({
        type: 'product',
        hs: `${code.slice(0,4)}.${code.slice(4)}`,
        name,
        sub: `HS ${code.slice(0,4)}.${code.slice(4)}`,
        icon: '📦',
      }));
    if (prefix6) {
      const fmt6     = `${prefix6.slice(0,4)}.${prefix6.slice(4)}`;
      const desc6    = HS_SUBHEADINGS[prefix6] || null;
      const specific = { type:'product', hs: clean, name: desc6 || `Subpartida ${fmt6}`, sub:`Búsqueda específica HS ${fmt6}`, icon:'📦' };
      return [specific, direct, ...subs];
    }
    return [direct, ...subs];
  }
  return null; // señal: usar Claude
}

// ── LandingPage principal ────────────────────────────────────────

const LP_COUNTRIES = [
  { iso:"all", name:"🌍 Todos los países" },
  { iso:"col", name:"Colombia 🇨🇴" }, { iso:"chn", name:"China 🇨🇳" },
  { iso:"usa", name:"Estados Unidos 🇺🇸" }, { iso:"deu", name:"Alemania 🇩🇪" },
  { iso:"mex", name:"México 🇲🇽" }, { iso:"esp", name:"España 🇪🇸" },
  { iso:"ita", name:"Italia 🇮🇹" }, { iso:"bra", name:"Brasil 🇧🇷" },
  { iso:"arg", name:"Argentina 🇦🇷" }, { iso:"per", name:"Perú 🇵🇪" },
  { iso:"chl", name:"Chile 🇨🇱" }, { iso:"gbr", name:"Reino Unido 🇬🇧" },
  { iso:"jpn", name:"Japón 🇯🇵" }, { iso:"kor", name:"Corea del Sur 🇰🇷" },
  { iso:"ind", name:"India 🇮🇳" }, { iso:"vnm", name:"Vietnam 🇻🇳" },
  { iso:"tur", name:"Turquía 🇹🇷" }, { iso:"can", name:"Canadá 🇨🇦" },
  { iso:"fra", name:"Francia 🇫🇷" }, { iso:"nld", name:"Países Bajos 🇳🇱" },
];

// nombre = texto que se muestra; buscar = término para ILIKE en DIAN (puede diferir del nombre oficial)
const PAISES_TOP = [
  { nombre:'China',              emoji:'🇨🇳', buscar:'China' },
  { nombre:'Estados Unidos',     emoji:'🇺🇸', buscar:'Estados Unidos' },
  { nombre:'Brasil',             emoji:'🇧🇷', buscar:'Brasil' },
  { nombre:'México',             emoji:'🇲🇽', buscar:'México' },
  { nombre:'Alemania',           emoji:'🇩🇪', buscar:'Alemania' },
  { nombre:'India',              emoji:'🇮🇳', buscar:'India' },
  { nombre:'Japón',              emoji:'🇯🇵', buscar:'Japón' },
  { nombre:'Corea del Sur',      emoji:'🇰🇷', buscar:'Corea' },
  { nombre:'Vietnam',            emoji:'🇻🇳', buscar:'Viet' },
  { nombre:'Italia',             emoji:'🇮🇹', buscar:'Italia' },
  { nombre:'Francia',            emoji:'🇫🇷', buscar:'Francia' },
  { nombre:'España',             emoji:'🇪🇸', buscar:'España' },
  { nombre:'Argentina',          emoji:'🇦🇷', buscar:'Argentina' },
  { nombre:'Chile',              emoji:'🇨🇱', buscar:'Chile' },
  { nombre:'Ecuador',            emoji:'🇪🇨', buscar:'Ecuador' },
  { nombre:'Perú',               emoji:'🇵🇪', buscar:'Perú' },
  { nombre:'Países Bajos',       emoji:'🇳🇱', buscar:'Países Bajos' },
  { nombre:'Reino Unido',        emoji:'🇬🇧', buscar:'Reino Unido' },
  { nombre:'Indonesia',          emoji:'🇮🇩', buscar:'Indonesia' },
  { nombre:'Canadá',             emoji:'🇨🇦', buscar:'Canadá' },
  { nombre:'Bélgica',            emoji:'🇧🇪', buscar:'Bélgica' },
  { nombre:'Suiza',              emoji:'🇨🇭', buscar:'Suiza' },
  { nombre:'Tailandia',          emoji:'🇹🇭', buscar:'Tailandia' },
  { nombre:'Taiwán',             emoji:'🇹🇼', buscar:'Taiwán' },
  { nombre:'Bolivia',            emoji:'🇧🇴', buscar:'Bolivia' },
  { nombre:'Irlanda',            emoji:'🇮🇪', buscar:'Irlanda' },
  { nombre:'Turquía',            emoji:'🇹🇷', buscar:'Turqu' },
  { nombre:'Rusia',              emoji:'🇷🇺', buscar:'Rusia' },
  { nombre:'Trinidad y Tobago',  emoji:'🇹🇹', buscar:'Trinidad' },
];

export default function LandingPage({ query, setQuery, operation, setOperation, onSearch, onLogistica, onCalculadora, onLoginClick, user, onSignOut, onDemo, isDemo = false, onPagarPro, isPaid = false }) {
  const [acItems,      setAcItems]      = useState([]);
  const [legalDoc,     setLegalDoc]     = useState(null); // 'privacidad' | 'terminos' | 'cookies'
  const [showPlan,     setShowPlan]     = useState(false);
  const [acLoading,    setAcLoading]    = useState(false);
  const [step2Visible, setStep2Visible] = useState(false);
  const [selHSCode,    setSelHSCode]    = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [selHSName,    setSelHSName]    = useState('');
  const [originIso,    setOriginIso]    = useState('chn');
  const [destIso,      setDestIso]      = useState('col');
  const [countryFocus, setCountryFocus] = useState(false);
  const [paisTipo,     setPaisTipo]     = useState('M'); // 'M'=importa desde 'X'=exporta hacia
  const debounceRef   = useRef(null);
  const currentValRef = useRef('');

  const scrollTo = id => document.getElementById(id)?.scrollIntoView({ behavior:'smooth' });

  const handleInput = val => {
    setQuery(val);
    currentValRef.current = val;
    if (!val.trim()) { setAcItems([]); setStep2Visible(false); return; }

    // NIT o HS — respuesta estática inmediata
    const staticResult = getStaticItems(val);
    if (staticResult !== null) {
      clearTimeout(debounceRef.current);
      setAcLoading(false);
      setAcItems(staticResult);
      return;
    }

    // Texto libre — búsqueda en 3 fases
    setAcLoading(true);
    setAcItems([]);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const snapshot = stripAccents(val.trim().toLowerCase()); // ignora tildes: "cafe" === "café"
      // Descarta esta respuesta si el usuario ya cambió el texto de búsqueda
      // (evita que una petición vieja y lenta sobreescriba resultados de una búsqueda nueva)
      const isStale = () => stripAccents(currentValRef.current.trim().toLowerCase()) !== snapshot;

      // ── Fase 0: partidas locales (instantáneo) ──
      // Check aliases first, then general search
      const looksLikeCompany = /\b(s\.a\.|sas|ltda|s\.a\.s|e\.s\.p\.)\b/i.test(snapshot);
      const aliasCodesRaw = looksLikeCompany ? [] : Object.entries(KEYWORD_ALIASES)
        .filter(([kw]) => hasWholeWord(snapshot, kw) || (kw.includes(snapshot) && kw.length - snapshot.length <= 2))
        .flatMap(([, codes]) => codes);
      const aliasCodes = [...new Set(aliasCodesRaw)];
      const aliasHs = aliasCodes
        .filter(code => HS_SUBHEADINGS[code])
        .map(code => ({
          type: 'product',
          hs:   code.slice(0,4) + '.' + code.slice(4,6),
          name: HS_SUBHEADINGS[code],
          sub:  'Partida arancelaria',
          icon: getHSIcon(code),
          local: true,
        }));
      const headingHs = Object.entries({ ...HS_CHAPTERS, ...HS_HEADINGS })
        .filter(([code, desc]) => stripAccents(desc.toLowerCase()).includes(snapshot))
        .slice(0, 4)
        .map(([code, desc]) => ({
          type: 'product',
          hs:   code.length === 2 ? code : code.slice(0,2) + '.' + code.slice(2),
          name: desc,
          sub:  'Partida arancelaria',
          icon: getHSIcon(code + '00'),
          local: true,
        }));
      const generalHs = [
        ...Object.entries(HS_SUBHEADINGS)
          .filter(([code, desc]) => stripAccents(desc.toLowerCase()).includes(snapshot) && !aliasCodes.includes(code))
          .map(([code, desc]) => ({
            type: 'product',
            hs:   code.slice(0,4) + '.' + code.slice(4,6),
            name: desc,
            sub:  'Partida arancelaria',
            icon: getHSIcon(code),
            local: true,
          })),
        ...headingHs,
      ];
      const localHs = [...aliasHs, ...generalHs].slice(0, 4);

      if (isStale() || getStaticItems(currentValRef.current) !== null) return;
      if (localHs.length > 0) setAcItems(localHs);

      // ── Fase 1: empresas ──
      // Si hay aliases HS, buscar por capítulo HS (encuentra top importadores del producto)
      // Si no, buscar por nombre de empresa
      let companyItems = [];
      try {
        const hsChapter = aliasCodes.length > 0
          ? aliasCodes[0].slice(0, 2)   // e.g. '851762' → '85'
          : null;
        const hsPrefix4 = aliasCodes.length > 0
          ? aliasCodes[0].slice(0, 4)   // e.g. '851762' → '8517'
          : null;
        const empresasData = await fetch('/api/empresas', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: hsChapter
            ? JSON.stringify({ action: 'byHs', hsChapter, hsPrefix: hsPrefix4, mode: operation })
            : JSON.stringify({ nombre: val.trim(), mode: operation }),
        }).then(r => r.json());
        companyItems = (Array.isArray(empresasData) ? empresasData : [])
          .slice(0, 4)
          .map(c => ({ type: 'company', nit: c.nit, name: c.nombre, city: 'Colombia', sector: operation === 'X' ? 'Exportador' : 'Importador' }));
      } catch { /* ignorar */ }

      if (isStale() || getStaticItems(currentValRef.current) !== null) return;
      // Partidas SIEMPRE primero: las empresas llegan tarde (async) y si se insertan
      // arriba desplazan la lista justo cuando el usuario va a hacer clic
      setAcItems([...localHs, ...companyItems]);

      // ── Fase 2: partidas Claude (lento, reemplaza locales) ──
      try {
        const hsText = await callClaude(
          'Experto en clasificación arancelaria colombiana. Usa nomenclatura HS 2022. Ejemplos clave: smartphones/celulares=8517.13 (NO 8517.12), laptops=8471.30, televisores=8528.72. Responde SOLO con JSON puro, sin markdown.',
          `Producto: "${val.trim()}". Dame 4 códigos HS de 6 dígitos con punto (formato XXXX.XX) más relevantes para Colombia en HS 2022.\nFormato exacto: {"suggestions":[{"code":"XXXX.XX","description":"nombre corto en español","sector":"sector"}]}`
        );
        const parsed = extractJSON(hsText);
        const claudeHs = (parsed.suggestions || []).slice(0, 4).map(s => ({
          type: 'product',
          hs:   s.code  || '0000',
          name: s.description || s.code,
          sub:  s.sector || '',
          icon: getHSIcon(s.code),
        }));
        if (isStale() || getStaticItems(currentValRef.current) !== null) return;
        if (claudeHs.length > 0) {
          setAcItems(prev => [...claudeHs, ...prev.filter(i => i.type === 'company')]);
        }
      } catch { /* ignorar */ }

      if (!isStale()) setAcLoading(false);
    }, 420);
  };

  const selectProduct = (hs, name) => {
    setQuery(`${hs} — ${name}`);
    setAcItems([]);
    setStep2Visible(false);
    onSearch({ hsCode: hs, hsName: name });
  };

  const handleSearch = () => {
    if (operation === 'P') {
      if (!query.trim()) return;
      setCountryFocus(false);
      setAcItems([]);
      const match = PAISES_TOP.find(p => p.nombre.toLowerCase() === query.trim().toLowerCase());
      onSearch({ pais: match ? match.buscar : query.trim(), paisDisplay: match ? match.nombre : query.trim(), paisTipo });
      return;
    }
    if (!query.trim()) return;
    const digits = query.trim().replace(/\D/g, '');

    // Partida con punto ("0901.11" / "0901.113000"): buscar por el código sin puntos
    // — ANTES del chequeo de NIT para que "0901.113000" no se confunda con un NIT
    if (/^\d{4}\.\d{1,6}$/.test(query.trim())) {
      setAcItems([]);
      onSearch({ hsCode: digits, hsName: `HS ${digits}` });
      return;
    }

    // NIT directo (excluye número puro de 10 dígitos = subpartida arancelaria nacional completa)
    const isPureDigitsLen10 = /^\d{10}$/.test(query.trim());
    if (!isPureDigitsLen10 && digits.length >= 8 && /^[\d.\s\-]+$/.test(query.trim())) {
      setAcItems([]);
      onSearch({ nit: digits });
      return;
    }

    // Código HS numérico directo (4–10 dígitos): buscar con ese código — antes de acLoading
    if (/^\d{4,10}$/.test(query.trim())) {
      const num = query.trim();
      setAcItems([]);
      onSearch({ hsCode: num, hsName: `HS ${num}` });
      return;
    }

    // Si el API aún está cargando, ir igual a búsqueda de producto
    // Priorizar siempre producto (HS) sobre empresa cuando el usuario escribe texto libre
    const firstProduct = acItems.find(i => i.type === 'product');
    if (firstProduct) {
      setAcItems([]);
      onSearch({ hsCode: firstProduct.hs, hsName: firstProduct.name });
      return;
    }

    if (selHSCode) {
      onSearch({ hsCode: selHSCode, hsName: selHSName });
      return;
    }

  };

  const handleAnalyze = () => {
    setAcItems([]);
    onSearch({ hsCode: selHSCode, hsName: selHSName, originIso, destIso });
  };

  const fillSearch = val => {
    setQuery(val);
    setAcItems(getStaticItems(val) || []);
    document.getElementById('hero-search')?.scrollIntoView({ behavior:'smooth', block:'center' });
  };

  const isTeal = operation === 'M';
  const btnBg  = isTeal ? C.teal : C.amber;

  return (
    <div style={{ fontFamily:"system-ui,'Segoe UI',sans-serif", background:'#F8FAFC', color:'#0A2540', minHeight:'100vh' }}>
      <style>{`
        @keyframes ticker-scroll { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
        .ticker-track { display:flex; width:max-content; animation:ticker-scroll 35s linear infinite; }
        .ticker-track:hover { animation-play-state:paused; }
        .lp-btn:hover { opacity:.88; }
        .lp-pill:hover { background:rgba(255,255,255,.15)!important; color:#fff!important; }
        .lp-ac-item:hover { background:#f0fdfa!important; }
        .lp-nav-link:hover { color:#0EA5A4!important; }
        .lp-footer-link:hover { color:#fff!important; }
        .lp-bento:hover { transform:translateY(-3px); box-shadow:0 16px 48px rgba(0,0,0,.12)!important; }
        .lp-feat-cell:hover { background:#FAFFFE!important; }
        .lp-plan-card { transition:transform .25s; }
        .lp-plan-card:hover { transform:translateY(-4px); }
      `}</style>

      {/* ── NAVBAR ── */}
      <nav style={{ position:'sticky', top:0, zIndex:1000, background:'rgba(255,255,255,0.93)', backdropFilter:'blur(20px)', WebkitBackdropFilter:'blur(20px)', borderBottom:'1px solid rgba(10,37,64,0.06)', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 40px', height:64 }}>
        <div style={{ display:'flex', alignItems:'center', gap:40 }}>
          <div style={{ fontSize:26, fontWeight:900, color:'#0A2540', letterSpacing:-1.5, cursor:'pointer' }} onClick={() => scrollTo('hero')}>
            K<span style={{ color:'#0EA5A4' }}>IN</span>
          </div>
          <div style={{ display:'flex', gap:28, alignItems:'center' }}>
            {[['Buscar','#bento'],['Precios','#pricing'],['Nosotros','#nosotros']].map(([label,href]) => (
              <a key={label} className="lp-nav-link" href={href} style={{ textDecoration:'none', color:'#64748B', fontSize:14, fontWeight:500, transition:'color .15s' }}>{label}</a>
            ))}
            <button className="lp-nav-link" onClick={onLogistica} style={{ background:'none', border:'none', color:'#64748B', fontSize:14, fontWeight:500, cursor:'pointer', fontFamily:'inherit', padding:0, transition:'color .15s' }}>Logística</button>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          {user ? (
            <>
              <span style={{ fontSize:13, color:'#64748B' }}>{user.email}</span>
              <button className="lp-btn" onClick={() => setShowPlan(true)} style={{ background:'transparent', border:'none', color:'#64748B', padding:'7px 10px', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>Mi plan</button>
              <button className="lp-btn" onClick={onSignOut} style={{ background:'transparent', border:'1px solid #E2E8F0', color:'#0A2540', padding:'7px 18px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>Cerrar sesión</button>
            </>
          ) : (
            <>
              <button className="lp-btn" onClick={onLoginClick} style={{ background:'transparent', border:'none', color:'#64748B', padding:'7px 16px', fontSize:14, fontWeight:500, cursor:'pointer', fontFamily:'inherit' }}>Iniciar sesión</button>
              <button className="lp-btn" onClick={onLoginClick} style={{ background:'#0A2540', color:'#fff', border:'none', padding:'9px 22px', borderRadius:9, fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>Comenzar gratis →</button>
            </>
          )}
        </div>
      </nav>

      {/* ── HERO ── */}
      <section id="hero" style={{ position:'relative', background:'#040D1A', padding:'108px 32px 88px', textAlign:'center' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:'radial-gradient(rgba(14,165,164,.13) 1px, transparent 1px)', backgroundSize:'32px 32px', pointerEvents:'none' }}/>
        <div style={{ position:'absolute', top:0, left:'50%', transform:'translateX(-50%)', width:900, height:500, background:'radial-gradient(ellipse at center top, rgba(14,165,164,.1) 0%, transparent 65%)', pointerEvents:'none' }}/>
        <div style={{ position:'relative', zIndex:500 }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:'rgba(14,165,164,.1)', border:'1px solid rgba(14,165,164,.22)', color:'#5EEAD4', fontSize:12, fontWeight:700, padding:'6px 18px', borderRadius:100, marginBottom:32, letterSpacing:.8, textTransform:'uppercase' }}>
            <span style={{ width:6, height:6, borderRadius:'50%', background:'#0EA5A4', display:'inline-block' }}/>
            Inteligencia de Comercio Exterior · Colombia
          </div>
          <h1 style={{ fontSize:'clamp(44px,6.5vw,76px)', fontWeight:900, lineHeight:1.07, color:'#fff', marginBottom:16, letterSpacing:-2.5 }}>
            Descubre quién importa<br/>y exporta
          </h1>
          <div style={{ fontSize:'clamp(38px,5.5vw,68px)', fontWeight:900, lineHeight:1.07, color:'#0EA5A4', marginBottom:24, letterSpacing:-2 }}>
            cualquier producto del mundo
          </div>
          <p style={{ fontSize:18, color:'#64748B', maxWidth:500, margin:'0 auto 52px', lineHeight:1.65 }}>
            Datos reales de DIAN, Comtrade, OMC y más —<br/>analizados por IA en segundos.
          </p>

        {/* Search card */}
        <div id="hero-search" style={{ background:'#fff', borderRadius:20, maxWidth:760, margin:'0 auto', boxShadow:'0 40px 100px rgba(0,0,0,.55), 0 0 0 1px rgba(255,255,255,.04)', display:'flex', overflow:'visible', position:'relative' }}>

          {/* Toggle vertical izquierda */}
          <div style={{ display:'flex', flexDirection:'column', padding:'10px 8px', gap:4, flexShrink:0 }}>
            {[['M','🛬','Importar'],['X','📦','Exportar'],['P','🌍','País']].map(([val,emoji,label]) => {
              const active = operation === val;
              const activeColor = val==='M' ? '#0EA5A4' : val==='X' ? '#D4A24C' : '#7c3aed';
              return (
                <button key={val} onClick={() => {
                  setOperation(val);
                  if (val === 'P') { setQuery(''); setAcItems([]); setCountryFocus(true); }
                }} style={{
                  display:'flex', alignItems:'center', gap:6,
                  padding:'10px 16px', border:'none', borderRadius:10,
                  fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
                  background: active ? activeColor : 'transparent',
                  color: active ? '#fff' : '#64748B',
                  transition:'all .2s', whiteSpace:'nowrap',
                }}>
                  <span>{emoji}</span> {label}
                </button>
              );
            })}
          </div>

          <div style={{ width:1, background:'#E2E8F0', margin:'16px 0' }} />

          {/* Input + botón */}
          <div style={{ flex:1, padding:'16px 16px 16px 20px', display:'flex', flexDirection:'column', gap:0 }}>
            {operation === 'P' ? (
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                <div style={{ fontSize:11, fontWeight:700, color:'#7c3aed', letterSpacing:.5, textTransform:'uppercase' }}>
                  ¿QUÉ PAÍS QUIERES ANALIZAR?
                </div>
                <div style={{ display:'flex', background:'#f5f3ff', borderRadius:8, padding:3, gap:2 }}>
                  {[['M','🛬 Importa desde'],['X','📦 Exporta hacia']].map(([t, label]) => (
                    <button key={t} onMouseDown={e => { e.preventDefault(); setPaisTipo(t); }}
                      style={{ background: paisTipo===t ? '#7c3aed' : 'transparent', color: paisTipo===t ? '#fff' : '#7c3aed',
                        border:'none', borderRadius:6, padding:'4px 10px', fontSize:11, fontWeight:700,
                        cursor:'pointer', fontFamily:'inherit', transition:'all .15s' }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ fontSize:11, fontWeight:700, color:'#94A3B8', letterSpacing:.6, textTransform:'uppercase', marginBottom:8 }}>
                {`¿QUÉ PRODUCTO QUIERES ${operation==='M' ? 'IMPORTAR' : 'EXPORTAR'}?`}
              </div>
            )}
            <div style={{ display:'flex', gap:10, alignItems:'center', position:'relative' }}>
              <input
                style={{ flex:1, border:'none', outline:'none', fontSize:22, fontWeight:600, color:'#0A2540', fontFamily:'inherit', background:'transparent', padding:0 }}
                placeholder={operation==='P' ? 'China, Estados Unidos, Vietnam...' : 'café, aguacate, HS 0901...'}
                value={query}
                onChange={e => operation==='P' ? setQuery(e.target.value) : handleInput(e.target.value)}
                onFocus={() => { if (operation==='P') setCountryFocus(true); }}
                onBlur={() => setTimeout(() => setCountryFocus(false), 180)}
                onKeyDown={e => e.key==='Enter' && handleSearch()}
                autoComplete="off"
              />
              <button className="lp-btn" onClick={handleSearch} style={{
                padding:'14px 28px', background: operation==='P' ? '#7c3aed' : acLoading ? '#94A3B8' : '#0A2540', color:'#fff',
                border:'none', borderRadius:12, cursor: acLoading ? 'wait' : 'pointer', fontWeight:800,
                fontSize:15, fontFamily:'inherit', whiteSpace:'nowrap', flexShrink:0,
              }}>
                {operation==='P' ? 'Ver país →' : acLoading ? 'Buscando…' : 'Buscar →'}
              </button>
              {isDemo && !user && (
                <div
                  onClick={onLoginClick}
                  style={{ position:'absolute', inset:0, cursor:'pointer', zIndex:10 }}
                  title="Crea una cuenta para buscar cualquier producto"
                />
              )}
            </div>
          </div>

          {/* Country dropdown — modo País */}
          {operation === 'P' && (countryFocus || query) && (() => {
            const q = query.trim().toLowerCase();
            const filtered = q
              ? PAISES_TOP.filter(p => p.nombre.toLowerCase().includes(q) || p.buscar.toLowerCase().includes(q))
              : PAISES_TOP;
            const showCustom = q && !filtered.find(p => p.nombre.toLowerCase() === q);
            if (!filtered.length && !showCustom) return null;
            return (
              <div style={{
                position:'absolute', top:'calc(100% + 8px)', left:0, right:0,
                background:C.white, borderRadius:16, overflow:'hidden',
                boxShadow:'0 12px 40px rgba(0,0,0,.18)', zIndex:600,
                border:`1px solid ${C.light}`, maxHeight:480, overflowY:'auto',
              }}>
                <div style={{ padding:'10px 16px 4px', fontSize:11, fontWeight:700, color:'#7c3aed', letterSpacing:.8, textTransform:'uppercase', background:'#faf5ff', position:'sticky', top:0 }}>
                  🌍 Países — importaciones y exportaciones Colombia
                </div>
                {filtered.map((p, i) => (
                  <div key={i} className="lp-ac-item"
                    onMouseDown={() => {
                      setQuery(p.nombre);
                      setCountryFocus(false);
                      onSearch({ pais: p.buscar || p.nombre, paisDisplay: p.nombre, paisTipo });
                    }}
                    style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 16px', cursor:'pointer', borderTop:`1px solid #f5f3ff` }}>
                    <div style={{ width:40, height:40, borderRadius:10, background:'#f5f3ff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, flexShrink:0 }}>
                      {p.emoji}
                    </div>
                    <div style={{ flex:1, textAlign:'left' }}>
                      <div style={{ fontSize:15, fontWeight:700, color:C.navy }}>{p.nombre}</div>
                      <div style={{ fontSize:12, color:C.grey, marginTop:1 }}>Ver comercio Colombia ↔ este país</div>
                    </div>
                    <span style={{ fontSize:12, fontWeight:700, padding:'4px 12px', borderRadius:6, background:'#7c3aed', color:'#fff' }}>
                      Ver →
                    </span>
                  </div>
                ))}
                {showCustom && (
                  <div className="lp-ac-item"
                    onMouseDown={() => {
                      setCountryFocus(false);
                      onSearch({ pais: query.trim(), paisTipo });
                    }}
                    style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 16px', cursor:'pointer', borderTop:`1px solid #f5f3ff`, background:'#faf5ff' }}>
                    <div style={{ width:40, height:40, borderRadius:10, background:'#ede9fe', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, flexShrink:0 }}>🔍</div>
                    <div style={{ flex:1, textAlign:'left' }}>
                      <div style={{ fontSize:15, fontWeight:700, color:'#7c3aed' }}>Buscar "{query.trim()}"</div>
                      <div style={{ fontSize:12, color:C.grey, marginTop:1 }}>Consultar en DIAN</div>
                    </div>
                    <span style={{ fontSize:12, fontWeight:700, padding:'4px 12px', borderRadius:6, background:'#7c3aed', color:'#fff' }}>
                      Ver →
                    </span>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Autocomplete dropdown */}
          {operation !== 'P' && (acItems.length > 0 || acLoading) && (() => {
            const companyItems = acItems.filter(i => i.type === 'company');
            const productItems = acItems.filter(i => i.type === 'product');
            return (
            <div style={{
              position:'absolute', top:'calc(100% + 8px)', left:0, right:0,
              background:C.white, borderRadius:16, overflow:'hidden',
              boxShadow:'0 12px 40px rgba(0,0,0,.18)', zIndex:600,
              border:`1px solid ${C.light}`,
            }}>
              {acLoading && acItems.length === 0 && (
                <div style={{ padding:'16px', textAlign:'center', color:C.grey, fontSize:13 }}>
                  <span style={{ display:'inline-block', animation:'spin 1s linear infinite', marginRight:8 }}>⏳</span>
                  Buscando empresas y clasificando con IA…
                </div>
              )}

              {/* ── EMPRESAS ── */}
              {companyItems.length > 0 && (<>
                <div style={{ padding:'10px 16px 4px', fontSize:11, fontWeight:700, color:C.teal, letterSpacing:.8, textTransform:'uppercase', background:'#f0fdfa' }}>
                  🏭 Empresas {operation === 'X' ? 'exportadoras' : 'importadoras'} en Colombia
                </div>
                {companyItems.map((item, i) => (
                  <div key={`c${i}`} className="lp-ac-item"
                    onClick={() => onSearch({ nit: item.nit.replace(/\D/g, ''), mode: operation })}
                    style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', cursor:'pointer', borderTop:`1px solid #f0fdfa`, background:'#fafffe' }}>
                    <div style={{ width:40, height:40, borderRadius:10, background:'#ccfbf1', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>🏭</div>
                    <div style={{ flex:1, textAlign:'left' }}>
                      <div style={{ fontSize:14, fontWeight:700, color:C.navy }}>{item.name}</div>
                      <div style={{ fontSize:12, color:C.grey, marginTop:2 }}>NIT {item.nit}</div>
                    </div>
                    <span style={{ fontSize:12, fontWeight:700, padding:'4px 12px', borderRadius:6, background:C.teal, color:'#fff' }}>
                      Ver perfil →
                    </span>
                  </div>
                ))}
              </>)}

              {/* ── PARTIDAS HS ── */}
              {acLoading && companyItems.length > 0 && (
                <div style={{ padding:'10px 16px', fontSize:12, color:C.grey, display:'flex', alignItems:'center', gap:8, borderTop:`1px solid ${C.light}` }}>
                  <span style={{ display:'inline-block', animation:'spin 1s linear infinite' }}>⏳</span>
                  Clasificando partidas arancelarias…
                </div>
              )}
              {productItems.length > 0 && (<>
                <div style={{ padding:'10px 16px 4px', fontSize:11, fontWeight:700, color:C.grey, letterSpacing:.8, textTransform:'uppercase', background:'#f8fafc', borderTop: companyItems.length ? `2px solid ${C.light}` : 'none' }}>
                  📦 Partidas arancelarias
                </div>
                {productItems.map((item, i) => (
                  <div key={`p${i}`} className="lp-ac-item"
                    onClick={() => selectProduct(item.hs, item.name)}
                    style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', cursor:'pointer', borderTop:`1px solid #f8fafc` }}>
                    <div style={{ width:40, height:40, borderRadius:10, background:'#f1f5f9', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>
                      {item.icon || '📦'}
                    </div>
                    <div style={{ flex:1, textAlign:'left' }}>
                      <div style={{ fontSize:14, fontWeight:700, color:C.navy }}>{item.name}</div>
                      <div style={{ fontSize:12, color:C.grey, marginTop:2 }}>{item.sub}</div>
                    </div>
                    <div style={{ textAlign:'right', flexShrink:0 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:C.grey, marginBottom:4 }}>HS {item.hs}</div>
                      <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:6, background:'#d1fae5', color:'#065f46' }}>Gratis</span>
                    </div>
                  </div>
                ))}
              </>)}
            </div>
            );
          })()}
        </div>

        {/* Step 2: de dónde → a dónde */}
        {step2Visible && (
          <div style={{ maxWidth:760, margin:'16px auto 0', background:'rgba(255,255,255,.1)', borderRadius:16, padding:'20px 24px', backdropFilter:'blur(4px)' }}>
            <div style={{ fontSize:12, color:'#94a3b8', marginBottom:14, fontWeight:600, letterSpacing:.5, textTransform:'uppercase' }}>
              ¿De dónde a dónde?
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16, flexWrap:'wrap' }}>
              {/* País origen */}
              <div style={{ flex:1, minWidth:160 }}>
                <div style={{ fontSize:11, color:'#94a3b8', marginBottom:4, fontWeight:600 }}>🏭 País origen (proveedor)</div>
                <select
                  value={originIso}
                  onChange={e => setOriginIso(e.target.value)}
                  style={{ width:'100%', padding:'10px 12px', borderRadius:10, border:'1.5px solid rgba(255,255,255,.3)', background:'rgba(255,255,255,.15)', color:C.white, fontSize:14, fontFamily:'inherit', cursor:'pointer', outline:'none' }}
                >
                  {LP_COUNTRIES.map(c => <option key={c.iso} value={c.iso} style={{ background:C.navy, color:C.white }}>{c.name}</option>)}
                </select>
              </div>
              {/* Flecha */}
              <div style={{ fontSize:22, color:C.teal, fontWeight:700, flexShrink:0, marginTop:16 }}>→</div>
              {/* País destino */}
              <div style={{ flex:1, minWidth:160 }}>
                <div style={{ fontSize:11, color:'#94a3b8', marginBottom:4, fontWeight:600 }}>🛒 País destino (comprador)</div>
                <select
                  value={destIso}
                  onChange={e => setDestIso(e.target.value)}
                  style={{ width:'100%', padding:'10px 12px', borderRadius:10, border:'1.5px solid rgba(255,255,255,.3)', background:'rgba(255,255,255,.15)', color:C.white, fontSize:14, fontFamily:'inherit', cursor:'pointer', outline:'none' }}
                >
                  {LP_COUNTRIES.map(c => <option key={c.iso} value={c.iso} style={{ background:C.navy, color:C.white }}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <button className="lp-btn" onClick={handleAnalyze} style={{ width:'100%', padding:'14px 0', background:btnBg, color:C.white, border:'none', borderRadius:12, cursor:'pointer', fontWeight:700, fontSize:15, fontFamily:'inherit' }}>
              Ver análisis completo →
            </button>
          </div>
        )}

        {/* Popular pills */}
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', justifyContent:'center', marginTop:32 }}>
          <span className="lp-pill" onClick={onDemo} style={{ background:'rgba(14,165,164,.15)', border:'1px solid rgba(14,165,164,.35)', color:'#5EEAD4', fontSize:13, padding:'7px 18px', borderRadius:100, cursor:'pointer', transition:'all .2s', fontWeight:600 }}>
            🌸 Ver demo · Flores
          </span>
          {[['☕ Café','Café'],['🥑 Aguacate','Aguacate'],['HS 0901','0901'],['NIT empresa','9001234567']].map(([label,val]) => (
            <span key={label} className="lp-pill" onClick={() => fillSearch(val)} style={{ background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,255,255,.1)', color:'#94A3B8', fontSize:13, padding:'7px 18px', borderRadius:100, cursor:'pointer', transition:'all .2s' }}>
              {label}
            </span>
          ))}
        </div>

        {/* Video explicativo */}
        <div style={{ maxWidth:760, margin:'56px auto 0' }}>
          <div style={{ fontSize:13, fontWeight:700, color:'#5EEAD4', letterSpacing:.5, textTransform:'uppercase', marginBottom:16 }}>
            ▶ Mira cómo funciona en 2 minutos
          </div>
          <div style={{ position:'relative', paddingTop:'56.25%', borderRadius:16, overflow:'hidden', boxShadow:'0 30px 80px rgba(0,0,0,.5), 0 0 0 1px rgba(255,255,255,.06)' }}>
            <iframe
              src="https://www.youtube.com/embed/86D9co-W5ZM"
              title="Cómo funciona KIN"
              style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', border:'none' }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
        </div>{/* close zIndex div */}
      </section>

      {/* ── TICKER ── */}
      <div style={{ background:'#0EA5A4', overflow:'hidden', padding:'11px 0' }}>
        <div className="ticker-track">
          {[
            '📦 SAMSUNG ELECTRONICS · Importó $48.2M · 2025','☕ FEDECAFÉ · Exportó $114M · 2025',
            '🚗 SOFASA S.A. · 18,432 declaraciones · 2024','💊 BAYER S.A. · Importó $92M · 2025',
            '🌹 FINCA FLOWERCARGO · Exportó $34M · 2025','📱 CLARO COLOMBIA · Importó $67M · 2025',
            '⚙️ CORONA S.A. · 12,840 ops · 2024','🛢️ ECOPETROL · Exportó $8.4B · 2024',
            '📦 SAMSUNG ELECTRONICS · Importó $48.2M · 2025','☕ FEDECAFÉ · Exportó $114M · 2025',
            '🚗 SOFASA S.A. · 18,432 declaraciones · 2024','💊 BAYER S.A. · Importó $92M · 2025',
            '🌹 FINCA FLOWERCARGO · Exportó $34M · 2025','📱 CLARO COLOMBIA · Importó $67M · 2025',
            '⚙️ CORONA S.A. · 12,840 ops · 2024','🛢️ ECOPETROL · Exportó $8.4B · 2024',
          ].map((item,i) => (
            <span key={i} style={{ display:'inline-block', padding:'0 28px', fontSize:12, fontWeight:700, color:'rgba(255,255,255,.9)', letterSpacing:.3 }}>
              {item}<span style={{ margin:'0 12px', opacity:.35 }}>·</span>
            </span>
          ))}
        </div>
      </div>

      {/* ── STATS ── */}
      <div style={{ background:'#0A2540', padding:'56px 32px' }}>
        <div style={{ maxWidth:960, margin:'0 auto', display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:0 }}>
          {[
            { num:'+16M', lbl:'Declaraciones DIAN',       sub:'Actualizado diariamente' },
            { num:'+50K',  lbl:'Empresas activas',          sub:'Importadores y exportadores' },
            { num:'+180',  lbl:'Países cubiertos',          sub:'Comtrade global' },
            { num:'< 3s',  lbl:'Respuesta promedio',        sub:'Análisis IA en tiempo real' },
          ].map(({ num, lbl, sub }, i) => (
            <div key={lbl} style={{ textAlign:'center', padding:'0 24px', borderRight: i<3 ? '1px solid rgba(255,255,255,.06)' : 'none' }}>
              <div style={{ fontSize:38, fontWeight:900, color:'#0EA5A4', letterSpacing:-1.5 }}>{num}</div>
              <div style={{ fontSize:14, fontWeight:700, color:'#fff', marginTop:6 }}>{lbl}</div>
              <div style={{ fontSize:12, color:'#64748B', marginTop:4 }}>{sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* __ old trust bar replaced above __ */}
      <div style={{ display:'none' }}>datos verificados
      </div>

      {/* ── PRODUCTOS ── */}
      <section id="bento" style={{ padding:'96px 32px', background:'#F8FAFC' }}>
        <div style={{ maxWidth:1100, margin:'0 auto' }}>
          <div style={{ marginBottom:48 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#0EA5A4', letterSpacing:1.2, textTransform:'uppercase', marginBottom:12 }}>Datos en vivo</div>
            <h2 style={{ fontSize:40, fontWeight:900, letterSpacing:-1.5, color:'#0A2540', marginBottom:12 }}>Productos más activos</h2>
            <p style={{ fontSize:16, color:'#64748B', maxWidth:480 }}>Haz clic en cualquier producto para ver su análisis completo.</p>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }}>
            {BENTO_PRODUCTS.map((p,i) => (
              <div key={i} className="lp-bento" onClick={() => fillSearch(p.name)}
                style={{ background:'#fff', borderRadius:16, padding:28, border:'1px solid #E2E8F0', cursor:'pointer', transition:'all .25s', position:'relative', overflow:'hidden' }}>
                <div style={{ position:'absolute', top:0, right:0, width:80, height:80, background:'radial-gradient(circle at top right,rgba(14,165,164,.05),transparent)', borderRadius:'0 16px 0 80px' }}/>
                <div style={{ fontSize:11, color:'#94A3B8', fontFamily:'monospace', marginBottom:10, letterSpacing:.5 }}>HS {p.hs}</div>
                <div style={{ fontSize:16, fontWeight:700, color:'#0A2540', marginBottom:14 }}>{p.name}</div>
                <div style={{ fontSize:30, fontWeight:900, color:'#0EA5A4', letterSpacing:-1 }}>{p.val}</div>
                <div style={{ fontSize:12, color: p.trendUp ? '#16a34a' : '#dc2626', marginTop:10, fontWeight:600 }}>{p.trendUp ? '↑' : '↓'} {p.trend}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section style={{ background:'#fff', padding:'96px 32px', borderTop:'1px solid #E2E8F0' }}>
        <div style={{ maxWidth:1100, margin:'0 auto' }}>
          <div style={{ marginBottom:56 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#0EA5A4', letterSpacing:1.2, textTransform:'uppercase', marginBottom:12 }}>Plataforma</div>
            <h2 style={{ fontSize:40, fontWeight:900, letterSpacing:-1.5, color:'#0A2540', marginBottom:12 }}>Todo lo que necesitas,<br/>en un solo lugar</h2>
            <p style={{ fontSize:16, color:'#64748B' }}>6 fuentes de datos. Un solo dashboard.</p>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:1, background:'#E2E8F0', border:'1px solid #E2E8F0', borderRadius:20, overflow:'hidden' }}>
            {FEATURES.map((f,i) => (
              <div key={i} className="lp-feat-cell" style={{ background:'#fff', padding:'36px 32px', transition:'background .2s' }}>
                <div style={{ fontSize:32, marginBottom:18 }}>{f.icon}</div>
                <div style={{ fontSize:15, fontWeight:700, color:'#0A2540', marginBottom:8 }}>{f.title}</div>
                <div style={{ fontSize:13, color:'#64748B', lineHeight:1.7 }}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PREVIEW ── */}
      <section id="preview" style={{ padding:'96px 32px', background:'#F8FAFC' }}>
        <div style={{ maxWidth:1100, margin:'0 auto' }}>
          <div style={{ marginBottom:40 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#0EA5A4', letterSpacing:1.2, textTransform:'uppercase', marginBottom:12 }}>Vista previa</div>
            <h2 style={{ fontSize:40, fontWeight:900, letterSpacing:-1.5, color:'#0A2540', marginBottom:12 }}>Así se ve un análisis real</h2>
            <p style={{ fontSize:16, color:'#64748B' }}>Café verde (HS 0901) · Desbloquea el 100% con Plan Pro.</p>
          </div>
          <DemoPanel />
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" style={{ background:'#0A2540', padding:'96px 32px' }}>
        <div style={{ maxWidth:1100, margin:'0 auto', textAlign:'center' }}>
          <div style={{ fontSize:12, fontWeight:700, color:'#0EA5A4', letterSpacing:1.2, textTransform:'uppercase', marginBottom:16 }}>Planes</div>
          <h2 style={{ fontSize:40, fontWeight:900, letterSpacing:-1.5, color:'#fff', marginBottom:12 }}>Simple. Transparente.<br/>Sin sorpresas.</h2>
          <p style={{ fontSize:16, color:'#64748B', marginBottom:32 }}>Sin contratos. Cancela cuando quieras.</p>

          {/* Toggle mensual / anual */}
          <div style={{ display:'inline-flex', background:'rgba(255,255,255,.06)', borderRadius:100, padding:4, marginBottom:48, gap:4 }}>
            <button onClick={() => setBillingCycle('monthly')} style={{
              padding:'8px 24px', borderRadius:100, border:'none', cursor:'pointer', fontFamily:'inherit',
              fontSize:14, fontWeight:600, transition:'all .2s',
              background: billingCycle === 'monthly' ? '#fff' : 'transparent',
              color: billingCycle === 'monthly' ? '#0A2540' : '#94A3B8',
            }}>Mensual</button>
            <button onClick={() => setBillingCycle('annual')} style={{
              padding:'8px 24px', borderRadius:100, border:'none', cursor:'pointer', fontFamily:'inherit',
              fontSize:14, fontWeight:600, transition:'all .2s', display:'flex', alignItems:'center', gap:8,
              background: billingCycle === 'annual' ? '#fff' : 'transparent',
              color: billingCycle === 'annual' ? '#0A2540' : '#94A3B8',
            }}>
              Anual
              <span style={{ background:'#0EA5A4', color:'#fff', fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:100 }}>-36%</span>
            </button>
          </div>

          <div style={{ display:'flex', gap:20, flexWrap:'wrap', justifyContent:'center', alignItems:'flex-start' }}>
            {PLANS.map((plan,i) => (
              <div key={i} className="lp-plan-card" style={{
                flex:1, minWidth:260, maxWidth:320, borderRadius:20, padding:'32px 28px', textAlign:'left',
                background: plan.featured ? '#fff' : 'rgba(255,255,255,.04)',
                border: plan.featured ? '2px solid #0EA5A4' : '1px solid rgba(255,255,255,.08)',
                boxShadow: plan.featured ? '0 0 0 4px rgba(14,165,164,.15),0 24px 60px rgba(0,0,0,.4)' : 'none',
                position:'relative',
              }}>
                {plan.featured && (
                  <div style={{ position:'absolute', top:-14, left:'50%', transform:'translateX(-50%)', background:'#0EA5A4', color:'#fff', fontSize:11, fontWeight:700, padding:'5px 16px', borderRadius:100, whiteSpace:'nowrap' }}>Más popular</div>
                )}
                <div style={{ fontSize:14, fontWeight:600, color: plan.featured ? '#64748B' : '#94A3B8', marginBottom:4 }}>{plan.name}</div>
                <div style={{ fontSize:44, fontWeight:900, color: plan.featured ? '#0A2540' : '#fff', letterSpacing:-2, marginBottom:4 }}>
                  {plan.featured ? (
                    billingCycle === 'annual'
                      ? <><span>$380</span><span style={{ fontSize:15, fontWeight:400, color:'#94A3B8' }}>/año</span></>
                      : <><span>$49</span><span style={{ fontSize:15, fontWeight:400, color:'#94A3B8' }}>/mes</span></>
                  ) : (
                    <>{plan.price}<span style={{ fontSize:15, fontWeight:400, color:'#94A3B8' }}>{plan.period}</span></>
                  )}
                </div>
                {plan.featured && billingCycle === 'annual' && (
                  <div style={{ fontSize:12, color:'#0EA5A4', fontWeight:600, marginBottom:4 }}>$31.7/mes · Ahorras $208 vs mensual</div>
                )}
                <div style={{ fontSize:13, color:'#94A3B8', margin:'8px 0 20px' }}>{plan.desc}</div>
                <div style={{ height:1, background: plan.featured ? '#E2E8F0' : 'rgba(255,255,255,.06)', marginBottom:20 }}/>
                <ul style={{ listStyle:'none', padding:0, margin:'0 0 28px', display:'flex', flexDirection:'column', gap:10 }}>
                  {plan.feats.map((f,j) => (
                    <li key={j} style={{ fontSize:13, display:'flex', alignItems:'center', gap:8, color: plan.featured ? '#0A2540' : '#94A3B8' }}>
                      <span style={{ color:'#0EA5A4', fontWeight:900, flexShrink:0 }}>✓</span>{f}
                    </li>
                  ))}
                </ul>
                <button className="lp-btn" onClick={() => {
                  if (plan.btnAction === 'demo') onDemo?.();
                  else if (plan.btnAction === 'signup') onPagarPro?.(billingCycle);
                  else if (plan.btnAction === 'contact') window.location.href = 'mailto:hola@kin-map.com';
                  else if (plan.btnAction === 'buscarEmpresa') document.getElementById('hero-search')?.scrollIntoView({ behavior:'smooth', block:'center' });
                }} style={{
                  width:'100%', padding:'13px 0', borderRadius:12, fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
                  background: plan.btnAction==='signup' ? '#0EA5A4' : 'transparent',
                  color:       plan.btnAction==='signup' ? '#fff' : '#94A3B8',
                  border:      plan.btnAction==='signup' ? 'none' : '1px solid rgba(255,255,255,.12)',
                }}>{plan.btnLabel}</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MISIÓN ── */}
      <section id="nosotros" style={{ background:'#040D1A', padding:'96px 32px', textAlign:'center', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:'radial-gradient(rgba(14,165,164,.08) 1px, transparent 1px)', backgroundSize:'32px 32px', pointerEvents:'none' }}/>
        <div style={{ maxWidth:680, margin:'0 auto', position:'relative', zIndex:1 }}>
          <div style={{ fontSize:12, fontWeight:700, color:'#0EA5A4', letterSpacing:1.2, textTransform:'uppercase', marginBottom:16 }}>Quiénes somos</div>
          <h2 style={{ fontSize:40, fontWeight:900, letterSpacing:-1.5, color:'#fff', marginBottom:12, lineHeight:1.15 }}>
            Transformamos datos de comercio exterior<br/><span style={{ color:'#0EA5A4' }}>en decisiones de negocio</span>
          </h2>
          <p style={{ fontSize:15, color:'#0EA5A4', marginBottom:24, fontWeight:500 }}>La información correcta, en el momento correcto</p>
          <p style={{ fontSize:17, color:'#64748B', lineHeight:1.8, marginBottom:40 }}>
            Procesamos millones de registros oficiales de la DIAN y fuentes internacionales. Nuestra plataforma es útil tanto para empresas colombianas que buscan crecer, como para compañías del exterior que quieren entender y operar en el mercado colombiano.
          </p>
          <div style={{ display:'flex', justifyContent:'center', gap:16, marginBottom:48, flexWrap:'wrap' }}>
            {[
              { icon:'📊', title:'Datos reales', desc:'Directamente de la DIAN, sin intermediarios' },
              { icon:'🔄', title:'Actualización constante', desc:'Información procesada y disponible al momento' },
              { icon:'🌎', title:'Colombia y el mundo', desc:'Para empresas locales e internacionales' },
            ].map(({ icon, title, desc }) => (
              <div key={title} style={{ background:'rgba(14,165,164,.07)', border:'1px solid rgba(14,165,164,.15)', borderRadius:12, padding:'20px 24px', textAlign:'left', maxWidth:200, flex:'1 1 160px' }}>
                <div style={{ fontSize:24, marginBottom:8 }}>{icon}</div>
                <div style={{ fontSize:14, fontWeight:700, color:'#fff', marginBottom:4 }}>{title}</div>
                <div style={{ fontSize:12, color:'#64748B', lineHeight:1.6 }}>{desc}</div>
              </div>
            ))}
          </div>
          <div style={{ marginBottom:40 }}>
            <a href="mailto:hola@kin-map.com" style={{ display:'inline-block', background:'#0EA5A4', color:'#fff', textDecoration:'none', padding:'14px 32px', borderRadius:10, fontSize:15, fontWeight:700, letterSpacing:.3 }}>
              hola@kin-map.com
            </a>
          </div>
          <div style={{ display:'flex', justifyContent:'center', gap:0 }}>
            {NOS_STATS.map(({ num, lbl }, i) => (
              <div key={lbl} style={{ textAlign:'center', padding:'0 32px', borderRight: i < NOS_STATS.length-1 ? '1px solid rgba(255,255,255,.08)' : 'none' }}>
                <div style={{ fontSize:32, fontWeight:900, color:'#0EA5A4' }}>{num}</div>
                <div style={{ fontSize:13, color:'#64748B', marginTop:4 }}>{lbl}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ background:'#040D1A', borderTop:'1px solid rgba(255,255,255,.05)', color:'#64748B', padding:'56px 40px 28px' }}>
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr', gap:40, maxWidth:1100, margin:'0 auto 40px' }}>
          <div>
            <div style={{ fontSize:28, fontWeight:900, color:'#fff', letterSpacing:-1.5, marginBottom:12 }}>K<span style={{ color:'#0EA5A4' }}>IN</span></div>
            <p style={{ fontSize:13, lineHeight:1.7, maxWidth:260 }}>Inteligencia de comercio exterior para Colombia y el mundo.</p>
            <div style={{ marginTop:16, fontSize:11, color:'rgba(255,255,255,.18)', letterSpacing:.5 }}>DIAN · UN Comtrade · OMC · World Bank · Claude AI</div>
          </div>
          {[
            { title:'Producto', links:[['Buscar productos','#bento'],['Precios','#pricing'],['API próximamente','#']] },
            { title:'Empresa',  links:[['Nosotros','#nosotros'],['Contacto','mailto:hola@kin-map.com']] },
            { title:'Legal',    links:[['Privacidad','#legal-privacidad'],['Términos de uso','#legal-terminos'],['Cookies','#legal-cookies']] },
          ].map(col => (
            <div key={col.title}>
              <h4 style={{ color:'#fff', fontSize:13, fontWeight:700, marginBottom:16, letterSpacing:.5 }}>{col.title}</h4>
              {col.links.map(([label,href]) => (
                <a key={label} className="lp-footer-link" href={href}
                  onClick={href.startsWith('#legal-') ? (e) => { e.preventDefault(); setLegalDoc(href.slice('#legal-'.length)); } : undefined}
                  style={{ display:'block', fontSize:13, color:'#64748B', textDecoration:'none', marginBottom:10, transition:'color .15s' }}>{label}</a>
              ))}
            </div>
          ))}
        </div>
        <div style={{ borderTop:'1px solid rgba(255,255,255,.05)', paddingTop:24, maxWidth:1100, margin:'0 auto', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontSize:12 }}>© 2026 KIN · Todos los derechos reservados</div>
          <div style={{ fontSize:12, color:'rgba(255,255,255,.15)' }}>Hecho con ♥ en Colombia</div>
        </div>
      </footer>

      {legalDoc && <LegalModal doc={legalDoc} onClose={() => setLegalDoc(null)} />}
      {showPlan && <PlanModal user={user} isPaid={isPaid} onClose={() => setShowPlan(false)} />}

    </div>
  );
}
